import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Calendar, Clock } from "lucide-react";
import { BLOG_POSTS, BLOG_BY_SLUG } from "../data/blog";
import { useSEO, articleSchema, breadcrumbSchema, SITE_URL } from "../lib/seo";

export default function ResourcesPage() {
  useSEO({
    title: "NDIS Resources & Guides | Alpha Community Care",
    description: "Plain-English NDIS guides for participants, families and support coordinators across Australia.",
    canonical: `${SITE_URL}/resources`,
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Resources", path: "/resources" }])],
  });
  return (
    <div data-testid="page-resources">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-14 pb-10">
          <div className="alpha-overline">Resources &amp; guides</div>
          <h1 className="mt-3 text-4xl sm:text-[52px] font-display font-medium tracking-tight leading-[1.05]">The NDIS, in plain English.</h1>
          <p className="mt-4 max-w-2xl text-[color:var(--alpha-mute)]">Practical articles for participants, families and coordinators. No jargon. No fluff.</p>
        </div>
      </section>

      <section className="alpha-section">
        <div className="alpha-container grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BLOG_POSTS.map((p) => (
            <Link key={p.slug} to={`/resources/${p.slug}`} className="alpha-card overflow-hidden group" data-testid={`blog-${p.slug}`}>
              <div className="h-44 overflow-hidden">
                <img src={p.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="alpha-overline">{p.cat}</div>
                <h3 className="mt-2 font-display text-lg font-semibold group-hover:text-[color:var(--alpha-purple-700)] leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm text-[color:var(--alpha-mute)] line-clamp-2">{p.excerpt}</p>
                <div className="mt-3 text-xs text-[color:var(--alpha-mute)] flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.date}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.read}</span>
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--alpha-purple-700)]">Read article <ArrowRight className="w-4 h-4" /></div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const p = BLOG_BY_SLUG[slug];
  useSEO({
    title: p ? `${p.title} | Alpha Community Care` : "Resources | Alpha Community Care",
    description: p?.excerpt,
    canonical: p ? `${SITE_URL}/resources/${p.slug}` : `${SITE_URL}/resources`,
    jsonLd: p ? [
      articleSchema({ title: p.title, description: p.excerpt, slug: p.slug, date: p.date, image: p.image }),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Resources", path: "/resources" },
        { name: p.title, path: `/resources/${p.slug}` },
      ]),
    ] : [],
  });
  if (!p) return <Navigate to="/resources" replace />;

  return (
    <div data-testid={`page-blog-${p.slug}`}>
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-10 pb-10">
          <Link to="/resources" className="inline-flex items-center gap-2 text-sm text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)]">
            <ArrowLeft className="w-4 h-4" /> Resources
          </Link>
          <div className="alpha-overline mt-5">{p.cat}</div>
          <h1 className="mt-3 text-3xl sm:text-[48px] font-display font-medium tracking-tight leading-[1.08] max-w-3xl">{p.title}</h1>
          <div className="mt-5 text-sm text-[color:var(--alpha-mute)] flex items-center gap-4">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {p.date}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.read}</span>
          </div>
        </div>
      </section>

      <section className="alpha-container py-12 grid lg:grid-cols-12 gap-10">
        <article className="lg:col-span-8">
          <div className="rounded-[24px] overflow-hidden aspect-[16/9] border border-[color:var(--alpha-line)]">
            <img src={p.image} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="mt-8 space-y-8">
            <p className="text-lg text-[color:var(--alpha-mute)] leading-relaxed">{p.excerpt}</p>
            {p.body.map(([t, d]) => (
              <div key={t}>
                <h2 className="font-display text-2xl font-medium">{t}</h2>
                <p className="mt-2 text-[color:var(--alpha-ink)] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </article>
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-32 space-y-5">
            <div className="alpha-card p-6 alpha-gradient text-white">
              <div className="alpha-overline !text-white/80">Ready to start?</div>
              <h3 className="mt-2 font-display text-xl font-semibold text-white">Talk to our intake team</h3>
              <p className="text-sm text-white/85 mt-2">Call 1300 380 692 or submit a referral online.</p>
              <div className="mt-5 space-y-2">
                <Link to="/referrals" className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-5 py-3 font-semibold text-center block">Make a referral</Link>
                <a href="tel:1300380692" className="border border-white/60 rounded-full px-5 py-3 font-semibold text-center block hover:bg-white/10">1300 380 692</a>
              </div>
            </div>

            <div className="alpha-card p-6">
              <div className="alpha-overline">More articles</div>
              <ul className="mt-3 space-y-2">
                {BLOG_POSTS.filter((x) => x.slug !== p.slug).slice(0, 4).map((x) => (
                  <li key={x.slug}>
                    <Link to={`/resources/${x.slug}`} className="text-sm hover:text-[color:var(--alpha-purple-700)]">{x.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
